import type { Lang } from '../lib/countries';
import { useI18n } from '../i18n';

type GameKey = 'wormillion' | 'stardewdle' | 'cascadle' | 'farhike';

interface RecommendedGame {
  key: GameKey;
  name: string;
  monogram: string;
  icon: string;
  urls: Partial<Record<Lang, string>>;
}

// Keep the locale matrix explicit: a missing locale means the card must not be
// rendered on that version of GeoDle. Stardewdle/Cascadle routes were checked
// against their hreflang declarations on 2026-08-17.
const GAMES: RecommendedGame[] = [
  {
    key: 'wormillion',
    name: 'Wormillion',
    monogram: 'W',
    icon: 'https://wormillion.com/icons/apple-touch-icon.png',
    // Only verified same-language destinations are listed; no fallback to English.
    urls: { en: 'https://wormillion.com/' },
  },
  {
    key: 'stardewdle',
    name: 'Stardewdle',
    monogram: 'SD',
    icon: 'https://stardewdle.org/icon-192.png',
    urls: {
      en: 'https://stardewdle.org/',
      fr: 'https://stardewdle.org/fr',
      de: 'https://stardewdle.org/de',
      pt: 'https://stardewdle.org/pt-BR',
    },
  },
  {
    key: 'cascadle',
    name: 'Cascadle',
    monogram: 'CA',
    icon: 'https://cascadle.net/apple-touch-icon.png',
    urls: { en: 'https://cascadle.net/' },
  },
  {
    key: 'farhike',
    name: 'Farhike',
    monogram: 'FH',
    icon: 'https://farhike.com/icon-192.png',
    urls: { en: 'https://farhike.com/' },
  },
];

export default function RecommendedGames({ lang }: { lang: Lang }) {
  const { t } = useI18n();
  const visibleGames = GAMES.flatMap((game) => {
    const href = game.urls[lang];
    return href ? [{ ...game, href }] : [];
  });

  // Locales without a matching destination should not leave an empty
  // heading behind when every recommendation is filtered out.
  if (visibleGames.length === 0) return null;

  return (
    <section className="recommendations" aria-labelledby="recommendations-title">
      <h2 id="recommendations-title">{t.recommendations.title}</h2>
      <ul className={`recommendations-grid count-${visibleGames.length}`}>
        {visibleGames.map((game) => {
          const description = t.recommendations.games[game.key];
          return (
            <li key={game.key}>
              <a
                className="recommendation-card"
                href={game.href}
                target="_blank"
                rel="noopener noreferrer external"
                aria-label={t.recommendations.open.replace('{name}', game.name)}
              >
                <span className="recommendation-icon" aria-hidden="true">
                  <span>{game.monogram}</span>
                  <img
                    src={game.icon}
                    alt=""
                    width={56}
                    height={56}
                    loading="lazy"
                    decoding="async"
                    referrerPolicy="no-referrer"
                    onError={(event) => {
                      event.currentTarget.style.display = 'none';
                    }}
                  />
                </span>
                <span className="recommendation-copy">
                  <b>{game.name}</b>
                  <span>{description}</span>
                </span>
                <span className="recommendation-arrow" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M7 17 17 7M8 7h9v9" />
                  </svg>
                </span>
              </a>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
